import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  ArrowRight, 
  Users,
  Zap,
  CreditCard,
  FileText,
  ShieldCheck,
  Building2,
  TrendingUp,
  Scale,
  Rocket,
  ArrowUpRight,
  Scissors,
  ShoppingBag,
  Utensils
} from 'lucide-react';
import { PRICING_CONFIG } from '../config/pricingConfig';
import CompoundGrowthSimulator from '../components/CompoundGrowthSimulator';

const carouselItems = [
  {
    image: "https://images.unsplash.com/photo-1542838132-92c53300491e?auto=format&fit=crop&q=80",
    title: "Market Funding, Real-World Support.",
    desc: "Empowering fruit vendors and market traders across the Republic.",
    category: "Market MSMEs",
    icon: ShoppingBag
  },
  {
    image: "https://images.unsplash.com/photo-1590579491624-f98f36d4c763?auto=format&fit=crop&q=80",
    title: "Capital for Every Kitchen.",
    desc: "Helping restaurant owners scale their flavor to every corner of Kenya.",
    category: "Hospitality",
    icon: Utensils
  },
  {
    image: "https://images.unsplash.com/photo-1534939561126-755e63857a29?auto=format&fit=crop&q=80",
    title: "Strengthening Essential Supply.",
    desc: "Providing butchery and essential service owners the tools to thrive.",
    category: "Retail & Supply",
    icon: Building2
  },
  {
    image: "https://images.unsplash.com/photo-1595475248540-c1a5f245ee6c?auto=format&fit=crop&q=80",
    title: "Braiding the Future.",
    desc: "Dedicated capital for beauty salons and service entrepreneurs.",
    category: "Service Sector",
    icon: Scissors
  }
];

const HeroCarousel = () => {
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % carouselItems.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="relative group hidden lg:block h-full min-h-[600px]">
      {/* Decorative Shadow/Background offset */}
      <div className="absolute inset-0 bg-slate-200 translate-x-6 translate-y-6 rounded-[3rem] opacity-40"></div>
      
      <div className="bg-white aspect-[4/5] rounded-[3rem] overflow-hidden relative shadow-2xl border border-white h-full">
        {carouselItems.map((item, idx) => (
          <div 
            key={idx}
            className={`absolute inset-0 transition-all duration-1000 ease-in-out ${
              idx === activeIndex ? 'opacity-100 scale-100' : 'opacity-0 scale-105 pointer-events-none'
            }`}
          >
            {/* Image Layer with Grayscale/Color blend */}
            <div className="absolute inset-0 overflow-hidden">
              <img 
                src={item.image} 
                alt={item.category} 
                className="w-full h-full object-cover grayscale brightness-90 contrast-110" 
              />
            </div>

            {/* Content Layer */}
            <div className="absolute inset-0 bg-gradient-to-t from-[#1e2335] via-[#1e2335]/60 to-transparent flex flex-col justify-end p-12 lg:p-16 text-left">
              <div className="w-16 h-1.5 bg-chui-yellow mb-8 rounded-full"></div>
              
              <div className="flex items-center gap-4 mb-4">
                 <div className="p-2 bg-chui-yellow/10 rounded-lg text-chui-yellow">
                   <item.icon size={20} strokeWidth={2.5} />
                 </div>
                 <span className="text-[10px] font-black uppercase tracking-[0.3em] text-chui-yellow/80">
                   {item.category}
                 </span>
              </div>

              <h3 className="text-4xl lg:text-5xl font-black text-white mb-6 leading-[1.1] tracking-tighter uppercase">
                {item.title}
              </h3>
              
              <p className="text-white/80 text-lg font-bold leading-relaxed max-w-sm mb-12">
                {item.desc}
              </p>

              <div className="flex items-center justify-between">
                <div className="flex gap-2">
                  {carouselItems.map((_, dotIdx) => (
                    <button 
                      key={dotIdx}
                      onClick={() => setActiveIndex(dotIdx)}
                      className={`h-1.5 rounded-full transition-all duration-500 ${
                        dotIdx === activeIndex ? 'w-10 bg-chui-yellow' : 'w-2 bg-white/20 hover:bg-white/40'
                      }`}
                    />
                  ))}
                </div>
                <div className="text-white/40 text-[9px] font-black uppercase tracking-[0.4em]">
                  {activeIndex + 1} / {carouselItems.length}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const FeaturesGrid = () => {
  const features = [
    {
      title: 'Fair Pricing',
      icon: Scale,
      headline: 'The Lowest Rates in Kenya',
      desc: 'Transparent growth tiers starting from 10.56% annual cost. We believe fair pricing is the foundation of character-based credit.',
      stats: '10.56% Annualized'
    },
    {
      title: 'Instant Speed',
      icon: Zap,
      headline: 'Decisions in 30 Minutes',
      desc: 'Our simplified intake coupled with local Guardian verification means you get an answer today, not next month.',
      stats: '~30 Min Decisions'
    },
    {
      title: 'Human Trust',
      icon: Users,
      headline: 'Character Over Paperwork',
      desc: 'We use cash flow and stewardship scores to assess your creditworthiness. We grow with you, step by step.',
      stats: 'No CRB Barriers'
    },
    {
      title: 'Growth Path',
      icon: Rocket,
      headline: 'Scale to National Level',
      desc: 'Move through 5 tiers and 18 tranches. As your business grows, your capital limits increase automatically.',
      stats: '5 Growth Tiers'
    }
  ];

  return (
    <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
      {features.map((f, i) => (
        <div key={i} className="bg-white p-10 lg:p-12 rounded-[2.5rem] border border-slate-200 shadow-sm hover:shadow-2xl hover:border-chui-navy/20 transition-all duration-500 group relative overflow-hidden flex flex-col items-start text-left">
          <div className="absolute top-0 right-0 p-8 opacity-[0.03] group-hover:opacity-[0.08] group-hover:scale-110 transition-all duration-700">
            <f.icon size={120} />
          </div>
          
          <div className="w-16 h-16 bg-slate-50 rounded-2xl flex items-center justify-center text-chui-navy mb-8 group-hover:bg-chui-navy group-hover:text-white transition-all duration-500 shadow-sm">
            <f.icon size={32} />
          </div>
          
          <div className="mb-auto">
            <h3 className="text-[10px] font-black text-chui-navy uppercase tracking-[0.4em] mb-3">Feature {i + 1}</h3>
            <h4 className="text-2xl lg:text-3xl font-black text-slate-900 mb-6 tracking-tighter uppercase">{f.headline}</h4>
            <p className="text-slate-600 font-bold leading-relaxed mb-8 text-lg">
              {f.desc}
            </p>
          </div>
          
          <div className="flex items-center justify-between w-full mt-4 pt-8 border-t border-slate-50">
            <span className="px-4 py-2 bg-slate-50 rounded-full border border-slate-100 text-[10px] font-black uppercase tracking-widest text-chui-navy">
              {f.stats}
            </span>
            <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-400 group-hover:bg-chui-navy group-hover:text-chui-yellow transition-all">
              <ArrowUpRight size={16} />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

const LandingPage: React.FC = () => {
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const targetId = hash.includes('/') ? hash.split('/').pop() : hash.replace('#', '');
      const element = document.getElementById(targetId || '');
      
      if (element) {
        setTimeout(() => {
          const offset = 100;
          const bodyRect = document.body.getBoundingClientRect().top;
          const elementRect = element.getBoundingClientRect().top;
          const elementPosition = elementRect - bodyRect;
          const offsetPosition = elementPosition - offset;
          window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
        }, 150);
      }
    }
  }, [hash]);

  return (
    <div className="bg-[#f8fafc]">
      {/* Hero Section */}
      <section className="pt-24 pb-32 lg:pt-36 lg:pb-48 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-24 items-stretch">
            <div className="text-center lg:text-left flex flex-col justify-center">
              <h1 className="text-6xl lg:text-[88px] font-black text-[#2d334a] mb-12 leading-[0.92] tracking-tighter uppercase">
                Fair & <br />
                <span className="text-slate-700">Transparent</span> <br />
                Business <br />
                Funding.
              </h1>
              <p className="text-[19px] text-slate-800 mb-14 leading-relaxed max-w-xl mx-auto lg:mx-0 font-bold">
                Chui Business Credit is the character-based finance partner for Kenya's MSMEs. Offering standardized growth tiers for established entrepreneurs.
              </p>
              
              <div className="flex flex-wrap justify-center lg:justify-start gap-6 mb-16">
                <Link to="/apply" className="bg-[#2d334a] text-white px-12 py-5 rounded-2xl font-black text-[15px] hover:bg-slate-800 transition-all shadow-2xl shadow-slate-300 transform hover:-translate-y-1 active:scale-95 uppercase tracking-wide">
                  Apply Now
                </Link>
                <Link to="/pricing-methodology" className="bg-white text-slate-900 border border-slate-300 px-12 py-5 rounded-2xl font-black text-[15px] hover:bg-slate-50 transition-all transform hover:-translate-y-1 uppercase tracking-wide">
                  View Rates
                </Link>
              </div>

              <div className="grid grid-cols-3 gap-8 max-w-lg mx-auto lg:mx-0 border-t border-slate-200 pt-10">
                <div>
                  <p className="text-2xl font-black text-chui-navy">10.56%</p>
                  <p className="text-[10px] font-black text-slate-600 uppercase tracking-widest mt-1">Target Rate</p>
                </div>
                <div>
                  <p className="text-2xl font-black text-chui-navy">30 Mins</p>
                  <p className="text-[10px] font-black text-slate-600 uppercase tracking-widest mt-1">Approval</p>
                </div>
                <div>
                  <p className="text-2xl font-black text-chui-navy">No CRB</p>
                  <p className="text-[10px] font-black text-slate-600 uppercase tracking-widest mt-1">Barrier</p>
                </div>
              </div>
            </div>

            <HeroCarousel />
          </div>
        </div>
      </section>

      {/* Simulator Section */}
      <section className="py-32 bg-white" id="calculator">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-24 max-w-3xl mx-auto">
            <h2 className="text-5xl font-black text-[#2d334a] mb-6 tracking-tighter uppercase">Growth Simulator</h2>
            <p className="text-xl text-slate-800 font-bold">Model your progression from Micro to National Enterprise.</p>
          </div>
          <CompoundGrowthSimulator />
        </div>
      </section>

      {/* Features Grid */}
      <section id="features" className="py-32 bg-slate-50 border-y border-slate-200 scroll-mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-24">
            <h2 className="text-5xl lg:text-6xl font-black text-[#2d334a] mb-8 tracking-tighter uppercase">The Chui Advantage</h2>
            <p className="text-xl text-slate-800 font-bold">Why MSMEs are switching from digital apps to Chui Business Credit.</p>
          </div>

          <FeaturesGrid />
        </div>
      </section>

      {/* Journey Section */}
      <section id="how-it-works" className="py-32 bg-white scroll-mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-8 items-stretch">
            <div className="lg:col-span-8 bg-white p-12 lg:p-20 rounded-[3rem] shadow-2xl shadow-slate-200 border border-slate-200 relative overflow-hidden">
              <div className="absolute top-0 right-0 p-8 opacity-[0.03]">
                <Building2 size={300} className="text-chui-navy" />
              </div>
              <h3 className="text-3xl font-black text-[#2d334a] mb-4 tracking-tighter uppercase">Your Journey with Chui</h3>
              <p className="text-slate-800 text-lg mb-24 font-bold">A simple, transparent process to get the funding you need.</p>
              
              <div className="relative flex justify-between items-start">
                <div className="absolute top-12 left-[10%] right-[10%] h-[2px] border-t-2 border-dashed border-slate-200"></div>
                
                {[
                  { icon: FileText, color: 'bg-blue-100 text-blue-700', label: "Quick Sign-Up", desc: "Register in seconds via our portal." },
                  { icon: Users, color: 'bg-yellow-100 text-yellow-800', label: "Verification", desc: "A local agent visits to verify your business." },
                  { icon: Zap, color: 'bg-purple-100 text-purple-700', label: "Get Scored", desc: "Unlock your credit tier based on cashflow." },
                  { icon: CreditCard, color: 'bg-green-100 text-green-700', label: "Access Funds", desc: "Funds disbursed directly to your wallet." }
                ].map((step, idx) => (
                  <div key={idx} className="relative z-10 flex flex-col items-center text-center w-1/4 group px-2">
                    <div className={`w-20 h-20 ${step.color} rounded-[2rem] flex items-center justify-center mb-10 shadow-xl shadow-slate-100 group-hover:scale-110 transition-transform duration-500 border border-white`}>
                      <step.icon size={30} strokeWidth={2.5} />
                    </div>
                    <h5 className="text-[12px] font-black text-slate-900 mb-4 tracking-tight uppercase tracking-widest">{step.label}</h5>
                    <p className="text-[10px] text-slate-700 leading-relaxed font-black uppercase">{step.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-4 bg-[#2d334a] p-12 lg:p-16 rounded-[3rem] text-white flex flex-col justify-center relative overflow-hidden shadow-2xl shadow-slate-400">
              <h3 className="text-3xl font-black mb-10 tracking-tight uppercase">Standard Pathway</h3>
              <ul className="space-y-8">
                {[
                  "5-tier pathway rewarding on-time repayment.",
                  "18-tranche model to release capital in cycles.",
                  "Daily micro-repayments to build stewardship.",
                  "90% on-time rule for tier qualification.",
                  "Guardian support for scaling coaching."
                ].map((item, i) => (
                  <li key={i} className="flex items-start text-white/90 text-[15px] font-bold leading-relaxed text-left">
                    <span className="w-2.5 h-2.5 rounded-full bg-chui-yellow mr-5 mt-1.5 shrink-0"></span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-48 bg-[#2d334a] text-center text-white relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1200px] h-[1200px] bg-white opacity-[0.03] rounded-full blur-[200px]"></div>
        </div>
        <div className="max-w-4xl mx-auto px-4 relative z-10">
          <h2 className="text-6xl lg:text-[88px] font-black mb-12 leading-[1.1] tracking-tighter uppercase">Empowering <br />your growth.</h2>
          <p className="text-2xl text-white/70 mb-20 font-bold">Join thousands of Kenyan MSMEs building the future with Chui.</p>
          <Link 
            to="/apply" 
            className="bg-white text-[#2d334a] px-16 py-7 rounded-[2rem] font-black text-2xl hover:bg-chui-yellow transition-all inline-flex items-center shadow-2xl shadow-black/40 active:scale-95 group uppercase"
          >
            Apply Now <ArrowRight size={32} className="ml-6 group-hover:translate-x-3 transition-transform duration-300" />
          </Link>
          <div className="mt-20 flex justify-center gap-12 opacity-50 text-[11px] font-black uppercase tracking-[0.4em]">
            <span>Secure Ecosystem</span>
            <span>Transparent Pricing</span>
            <span>Fast Approval</span>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LandingPage;